import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { BasicLeadForm } from "@component/assets/AbstractLeadForm/Steps/BasicLeadForm";
import { BasicSuccessPage } from "@component/assets/AbstractLeadForm/Steps/BasicSuccessPage";
import type { LeadRequest, ProductType } from "@component/assets/AbstractLeadForm/types";
import { JsonDataBuilder } from "@utils/JsonDataBuilder";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import type { ReactNode } from "react";
import React from "react";
import * as Step from "./Steps";
import { calculate } from "./domain";
import type {
	FormFields,
	LoanInsuranceLeadCalcRequest,
	LoanInsuranceLeadCalcResponse,
	LoanInsuranceResponse,
} from "./types";
import "./style.scss";

interface State<TFormFields extends FormFields = FormFields> extends AbstractLeadFormState<TFormFields> {
	calculatorResponse: LoanInsuranceResponse;
}

interface Props extends AbstractLeadFormProps<LoanInsuranceLeadCalcRequest, LoanInsuranceLeadCalcResponse> {
	variant?: string;
}

/**
 * @Shortcode
 *
 * @title "Hitelfedezeti biztosítás kalkulátor"
 * @shortcode "installment_protection_small"
 * @slug "hitelfedezeti-biztositas"
 * @type "calculator"
 */
export class LoanInsuranceSmallCalculator extends AbstractLeadForm<
	LoanInsuranceLeadCalcRequest,
	LoanInsuranceLeadCalcResponse,
	FormFields,
	Props,
	State
> {
	// EventParams
	protected readonly productTypes: string = "LOAN_INSURANCE";
	protected readonly componentType: EventComponentType = "small-calculator";

	public static defaultProps = {
		title: "Kérj ingyenes segítséget szakértőnktől!",
		outsideTitle: "Csoportos Személybiztosítás",
		outsideTitleLink: "/vedelem/bankmonitor-biztonsag/",
		bottomLogo: true,
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			calculatorResponse: undefined,
		};
	}

	protected renderSteps(): ReactNode {
		const {
			values: { email },
			submitState,
		} = this.state;
		const { variant } = this.props;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="step-2" variant={variant} />
				<Step.Step2 stepName="step-2" defaultTarget="result" />
				<Step.Result stepName="result" defaultTarget="lead-form" />
				<BasicLeadForm
					stepName="lead-form"
					defaultTarget="success-page"
					submitState={submitState}
					title={
						<>
							Add meg az elérhetőséged, és elküldjük a{" "}
							<span className="text-uppercase">hitelfedezeti biztosítás</span> részleteit emailben!
						</>
					}
					button="Eredmény küldése"
					crossSellOption
					newsletterOption
				/>
				<BasicSuccessPage stepName="success-page" subscriptionTypes={["RETAIL_NEWSLETTER"]} userEmail={email} />
			</>
		);
	}

	protected getLeadData(): LeadRequest<LoanInsuranceLeadCalcRequest, LoanInsuranceLeadCalcResponse> {
		const {
			values: { question },
		} = this.state;
		const data = super.getLeadData();

		data.request.question = question;

		return data;
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "result":
				this.onResult(target);
				break;
			case "success-page":
				this.submitLead(target);
				break;
			default:
				super.onStep(target, withEvent);
		}
	}

	private submitLead(target: string): void {
		this.sendLead()
			.then(() => {
				super.onStep(target, false);
				this.setState({
					submitState: undefined,
				});
			})
			.catch((error: Error) => {
				this.setState(
					{
						submitState: "error",
					},
					() => {
						this.promisePool.addTimeout().then(() => {
							this.setState({
								submitState: undefined,
							});
						});
					},
				);
				logError(error);
			});
	}

	private onResult(target: string): void {
		const { values } = this.state;

		this.setState({ submitState: "loading" });
		this.promisePool
			.add(calculate(values))
			.then((calculatorResponse: LoanInsuranceResponse) => {
				this.setState(
					{
						calculatorResponse,
						submitState: undefined,
					},
					() => {
						super.onStep(target, true);
					},
				);

				this.sendCalculationEvent([]);
			})
			.catch((error: Error) => {
				this.setState({ submitState: "error" });

				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				logError(error);
			});
	}

	protected getCalculatorRequest(): LoanInsuranceLeadCalcRequest {
		const {
			values: { birthYear, relatedBabyLoanAmount, relatedMortgageLoanAmount, relatedPersonalLoanAmount, fears },
		} = this.state;

		const otherData = new JsonDataBuilder();
		otherData.addItem(
			"Tart, hogy megromlik az egészsége",
			"afraidOfDecliningHealth",
			fears.includes("afraidOfDecliningHealth"),
			"bool",
		);
		otherData.addItem(
			"Tart a váratlan halálesettől",
			"afraidOfUnexpectedDeath",
			fears.includes("afraidOfUnexpectedDeath"),
			"bool",
		);
		otherData.addItem(
			"Tart a munkaelvesztésétől",
			"afraidOfLosingJob",
			fears.includes("afraidOfLosingJob"),
			"bool",
		);
		otherData.addItem("Lakáshitel összege", "relatedMortgageLoanAmount", relatedMortgageLoanAmount, "ft");
		otherData.addItem("Babaváróhitel összege", "relatedBabyLoanAmount", relatedBabyLoanAmount, "ft");
		otherData.addItem("Személyikölcsön", "relatedPersonalLoanAmount", relatedPersonalLoanAmount, "ft");

		return {
			...super.getCalculatorRequest(),
			birthYear,
			relatedBabyLoanAmount,
			relatedMortgageLoanAmount,
			relatedPersonalLoanAmount,
			preferredProduct: "Törlesztési védelem",
			afraidOfLosingJob: fears.includes("afraidOfLosingJob"),
			afraidOfDecliningHealth: fears.includes("afraidOfDecliningHealth"),
			afraidOfUnexpectedDeath: fears.includes("afraidOfUnexpectedDeath"),
			otherDataJson: otherData.getJson(),
		};
	}

	protected getCalculatorResponse(): LoanInsuranceLeadCalcResponse {
		const {
			calculatorResponse: { installments },
		} = this.state;

		return {
			...super.getCalculatorResponse(),
			...installments,
		};
	}

	protected getAutoresponderName(): string {
		return undefined;
	}

	protected getAutoresponderFields(): Record<string, any> {
		return {};
	}

	protected getProductType(): ProductType {
		return "LOAN_INSURANCE";
	}

	protected getClassName(): string {
		return `${super.getClassName()} installment-protection-small-calculator`;
	}

	protected getComponentName(): string {
		return "LoanInsuranceSmallCalculator";
	}
}

export default LoanInsuranceSmallCalculator;
